import React, { useState } from 'react';
import { Database, Download, Upload, HardDrive, AlertTriangle, CheckCircle, FileText } from 'lucide-react';
import { exportInventoryToExcel, importInventoryFromExcel } from '../utils/exportUtils';
import apiClient, { productosAPI, backupAPI } from '../api/client';
import { saveAs } from 'file-saver';

const BackupPage = () => {
  // Debug check
  React.useEffect(() => {
    console.log('API Methods:', {
      importBatch: productosAPI.importBatch,
      downloadBackup: backupAPI?.download
    });
  }, []);

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const exportarInventario = async () => {
    setLoading(true);
    try {
      const response = await productosAPI.getAll();
      if (response.data.success) {
        exportInventoryToExcel(response.data.data);
        alert('Inventario exportado exitosamente');
      }
    } catch (error) {
      alert('Error al exportar inventario: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const importarInventario = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      const productos = await importInventoryFromExcel(file);
      
      if (productos.length === 0) {
        alert('El archivo no contiene productos válidos');
        return;
      }

      const confirmacion = window.confirm(
        `Se han leído ${productos.length} productos del archivo. ¿Desea importarlos a la base de datos?`
      );

      if (!confirmacion) {
        setLoading(false);
        return;
      }

      // Simular progreso de carga visual
      let progreso = 0;
      const interval = setInterval(() => {
        progreso = Math.min(progreso + 10, 90);
        setUploadProgress(progreso);
      }, 200);

      // Usar apiClient directamente para evitar problemas de caché con productosAPI
      const response = await apiClient.post('/importar_productos.php', productos);
      
      clearInterval(interval);
      setUploadProgress(100);

      if (response.data.success) {
         const stats = response.data.stats;
         alert(`Importación exitosa:\n- Insertados: ${stats.insertados}\n- Actualizados: ${stats.actualizados}\n- Errores: ${stats.errores}`);
      } else {
         alert('Error en la importación: ' + response.data.message);
      }
      
      setUploadProgress(0);
    } catch (error) {
      console.error(error);
      alert('Error al importar inventario: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      // Limpiar input
      e.target.value = null;
    }
  };

  const crearBackupCompleto = async () => {
    if (!window.confirm('¿Desea descargar un backup completo de la base de datos?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await backupAPI.download();
      
      // Crear nombre de archivo con fecha
      const date = new Date();
      const fileName = `backup_eleventa_${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getDate().toString().padStart(2,'0')}_${date.getHours().toString().padStart(2,'0')}${date.getMinutes().toString().padStart(2,'0')}.sql`;
      
      saveAs(new Blob([response.data]), fileName);
      
      alert('Backup descargado exitosamente.');
    } catch (error) {
      console.error(error);
      alert('Error al descargar backup: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const restaurarBackup = () => {
    alert('Funcionalidad de restauración debe ejecutarse desde el servidor por seguridad.');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Backup y Copia de Seguridad</h1>
          <p className="text-gray-600 mt-1">Gestión de respaldos e importación/exportación de datos</p>
        </div>
      </div>

      {/* Advertencia */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3" />
          <div>
            <p className="text-sm text-yellow-700">
              <strong>Importante:</strong> Realiza backups periódicos para proteger tus datos. 
              Se recomienda hacer backup al menos una vez por semana.
            </p>
          </div>
        </div>
      </div>

      {/* Grid de opciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Backup Completo */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Backup Completo</h3>
              <p className="text-sm text-gray-600">Base de datos completa</p>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Crea una copia completa de toda la base de datos incluyendo productos, ventas, 
              clientes, proveedores y configuración.
            </p>

            <button
              onClick={crearBackupCompleto}
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download className="w-5 h-5" />
              Crear Backup Completo
            </button>

            <button
              onClick={restaurarBackup}
              className="w-full btn-secondary flex items-center justify-center gap-2"
            >
              <Upload className="w-5 h-5" />
              Restaurar Backup
            </button>
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Última copia:</strong> No disponible
            </p>
          </div>
        </div>

        {/* Exportar Inventario */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Exportar Inventario</h3>
              <p className="text-sm text-gray-600">Excel (.xlsx)</p>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Exporta todos los productos del inventario a un archivo Excel para análisis 
              o respaldo externo.
            </p>

            <button
              onClick={exportarInventario}
              disabled={loading}
              className="w-full btn-success flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download className="w-5 h-5" />
              Exportar a Excel
            </button>
          </div>

          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <p className="text-xs text-green-700">
                Incluye SKU, códigos, precios y stock
              </p>
            </div>
          </div>
        </div>

        {/* Importar Inventario */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Upload className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Importar Inventario</h3>
              <p className="text-sm text-gray-600">Desde Excel (.xlsx)</p>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Importa productos masivamente desde un archivo Excel. El archivo debe tener 
              las columnas: SKU, Nombre, Precio, Stock.
            </p>

            <label className="w-full btn-primary flex items-center justify-center gap-2 cursor-pointer">
              <Upload className="w-5 h-5" />
              Seleccionar Archivo Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={importarInventario}
                className="hidden"
                disabled={loading}
              />
            </label>

            {uploadProgress > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>

          <div className="mt-4 p-3 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-700">
              <strong>Formato requerido:</strong> SKU | Código de Barras | Nombre | Precio Venta | Stock
            </p>
          </div>
        </div>

        {/* Espacio en Disco */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-gray-100 rounded-lg">
              <HardDrive className="w-8 h-8 text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Información del Sistema</h3>
              <p className="text-sm text-gray-600">Estado del servidor</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Espacio usado:</span>
              <span className="text-sm font-medium text-gray-900">~50 MB</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Total de productos:</span>
              <span className="text-sm font-medium text-gray-900">Cargando...</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Total de ventas:</span>
              <span className="text-sm font-medium text-gray-900">Cargando...</span>
            </div>

            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Último backup:</span>
              <span className="text-sm font-medium text-gray-900">No disponible</span>
            </div>
          </div>
        </div>
      </div>

      {/* Instrucciones */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">📚 Instrucciones de Uso</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Exportar Inventario:</h4>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Click en "Exportar a Excel"</li>
              <li>El archivo se descargará automáticamente</li>
              <li>Abre con Excel, Google Sheets o LibreOffice</li>
              <li>Modifica los datos según necesites</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Importar Inventario:</h4>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Prepara tu archivo Excel con el formato correcto</li>
              <li>Click en "Seleccionar Archivo Excel"</li>
              <li>Selecciona tu archivo .xlsx</li>
              <li>Confirma la importación</li>
              <li>Espera a que termine el proceso</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Crear Backup:</h4>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Click en "Crear Backup Completo"</li>
              <li>Confirma la operación</li>
              <li>El backup se guarda en el servidor</li>
              <li>Se recomienda hacer backup semanal</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Restaurar Backup:</h4>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Contacta al administrador del sistema</li>
              <li>La restauración debe hacerse desde el servidor</li>
              <li>Requiere acceso directo a la base de datos</li>
              <li>Se recomienda probar en ambiente de prueba primero</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupPage;
